import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { Loader2, Save, Shield, Lock, Timer, AlertTriangle } from 'lucide-react';
import type { Json } from '@/integrations/supabase/types';

interface SecuritySettingsData {
  sessionTimeoutMinutes: number;
  maxLoginAttempts: number;
  lockoutDurationMinutes: number;
  minPasswordLength: number;
  requireUppercase: boolean;
  requireLowercase: boolean;
  requireNumbers: boolean;
  requireSpecialChars: boolean;
}

const DEFAULT_SETTINGS: SecuritySettingsData = {
  sessionTimeoutMinutes: 480,
  maxLoginAttempts: 5,
  lockoutDurationMinutes: 15,
  minPasswordLength: 8,
  requireUppercase: true,
  requireLowercase: true,
  requireNumbers: true,
  requireSpecialChars: true,
};

export const SecuritySettings: React.FC = () => {
  const [settings, setSettings] = useState<SecuritySettingsData>(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [originalSettings, setOriginalSettings] = useState<SecuritySettingsData>(DEFAULT_SETTINGS);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('app_settings')
        .select('value')
        .eq('key', 'security')
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      if (data?.value) {
        const loadedSettings = data.value as unknown as SecuritySettingsData;
        setSettings(loadedSettings);
        setOriginalSettings(loadedSettings);
      }
    } catch (e) {
      console.error('Failed to load security settings:', e);
      toast.error('Failed to load security settings');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = <K extends keyof SecuritySettingsData>(
    key: K,
    value: SecuritySettingsData[K]
  ) => {
    setSettings(prev => {
      const updated = { ...prev, [key]: value };
      setHasChanges(JSON.stringify(updated) !== JSON.stringify(originalSettings));
      return updated;
    });
  };

  const handleSave = async () => {
    // Validate settings
    if (settings.minPasswordLength < 6 || settings.minPasswordLength > 128) {
      toast.error('Password length must be between 6 and 128 characters');
      return;
    }
    if (settings.sessionTimeoutMinutes < 5 || settings.sessionTimeoutMinutes > 10080) {
      toast.error('Session timeout must be between 5 minutes and 7 days');
      return;
    }
    if (settings.maxLoginAttempts < 1 || settings.maxLoginAttempts > 100) {
      toast.error('Max login attempts must be between 1 and 100');
      return;
    }
    if (settings.lockoutDurationMinutes < 1 || settings.lockoutDurationMinutes > 1440) {
      toast.error('Lockout duration must be between 1 minute and 24 hours');
      return;
    }

    setSaving(true);
    try {
      const { error } = await supabase
        .from('app_settings')
        .update({
          value: settings as unknown as Json,
          description: 'Security and session settings',
        })
        .eq('key', 'security');

      if (error) throw error;

      setOriginalSettings(settings);
      setHasChanges(false);
      toast.success('Security settings saved');
    } catch (e) {
      console.error('Failed to save security settings:', e);
      toast.error('Failed to save security settings');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setSettings(originalSettings);
    setHasChanges(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Session Settings */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Timer className="w-5 h-5 text-primary" />
            <CardTitle className="text-lg">Session Settings</CardTitle>
          </div>
          <CardDescription>
            Configure user session behavior and timeouts
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="sessionTimeout">Session Timeout (minutes)</Label>
              <Input
                id="sessionTimeout"
                type="number"
                min={5}
                max={10080}
                value={settings.sessionTimeoutMinutes}
                onChange={(e) => handleChange('sessionTimeoutMinutes', parseInt(e.target.value) || 480)}
              />
              <p className="text-xs text-muted-foreground">
                Users will be logged out after this period of inactivity (5 min - 7 days)
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Login Security */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-primary" />
            <CardTitle className="text-lg">Login Security</CardTitle>
          </div>
          <CardDescription>
            Configure brute-force protection and login attempt limits
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="maxAttempts">Max Login Attempts</Label>
              <Input
                id="maxAttempts"
                type="number"
                min={1}
                max={100}
                value={settings.maxLoginAttempts}
                onChange={(e) => handleChange('maxLoginAttempts', parseInt(e.target.value) || 5)}
              />
              <p className="text-xs text-muted-foreground">
                Number of failed attempts before lockout
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="lockoutDuration">Lockout Duration (minutes)</Label>
              <Input
                id="lockoutDuration"
                type="number"
                min={1}
                max={1440}
                value={settings.lockoutDurationMinutes}
                onChange={(e) => handleChange('lockoutDurationMinutes', parseInt(e.target.value) || 15)}
              />
              <p className="text-xs text-muted-foreground">
                How long users are locked out after max attempts
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Password Policy */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Lock className="w-5 h-5 text-primary" />
            <CardTitle className="text-lg">Password Policy</CardTitle>
          </div>
          <CardDescription>
            Set minimum requirements for user passwords
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="minPasswordLength">Minimum Password Length</Label>
            <Input
              id="minPasswordLength"
              type="number"
              min={6}
              max={128}
              value={settings.minPasswordLength}
              onChange={(e) => handleChange('minPasswordLength', parseInt(e.target.value) || 8)}
              className="w-32"
            />
          </div>

          <Separator className="my-4" />

          <div className="space-y-3">
            <Label className="text-sm font-medium">Required Character Types</Label>
            
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="flex items-center justify-between p-3 rounded-lg border bg-muted/30">
                <div>
                  <Label htmlFor="requireUppercase" className="font-normal cursor-pointer">
                    Uppercase letters (A-Z)
                  </Label>
                </div>
                <Switch
                  id="requireUppercase"
                  checked={settings.requireUppercase}
                  onCheckedChange={(checked) => handleChange('requireUppercase', checked)}
                />
              </div>
              
              <div className="flex items-center justify-between p-3 rounded-lg border bg-muted/30">
                <div>
                  <Label htmlFor="requireLowercase" className="font-normal cursor-pointer">
                    Lowercase letters (a-z)
                  </Label>
                </div>
                <Switch
                  id="requireLowercase"
                  checked={settings.requireLowercase}
                  onCheckedChange={(checked) => handleChange('requireLowercase', checked)}
                />
              </div>
              
              <div className="flex items-center justify-between p-3 rounded-lg border bg-muted/30">
                <div>
                  <Label htmlFor="requireNumbers" className="font-normal cursor-pointer">
                    Numbers (0-9)
                  </Label>
                </div>
                <Switch
                  id="requireNumbers"
                  checked={settings.requireNumbers}
                  onCheckedChange={(checked) => handleChange('requireNumbers', checked)}
                />
              </div>
              
              <div className="flex items-center justify-between p-3 rounded-lg border bg-muted/30">
                <div>
                  <Label htmlFor="requireSpecialChars" className="font-normal cursor-pointer">
                    Special characters (!@#$...)
                  </Label>
                </div>
                <Switch
                  id="requireSpecialChars"
                  checked={settings.requireSpecialChars}
                  onCheckedChange={(checked) => handleChange('requireSpecialChars', checked)}
                />
              </div>
            </div>
          </div>

          {/* Warning if password policy is weak */}
          {(!settings.requireUppercase || !settings.requireLowercase || !settings.requireNumbers || !settings.requireSpecialChars || settings.minPasswordLength < 8) && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-destructive/10 border border-destructive/30 text-destructive">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <p className="text-sm">
                Weak password policies may increase the risk of unauthorized access.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Save/Reset Actions */}
      {hasChanges && (
        <div className="flex items-center justify-end gap-2 p-4 rounded-lg border bg-muted/30 sticky bottom-4">
          <span className="text-sm text-muted-foreground mr-auto">
            You have unsaved changes
          </span>
          <Button variant="outline" onClick={handleReset}>
            Discard
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Changes
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
};
